/**
 * The operations view: what the pipeline did, what it dropped and why, and what is stuck.
 *
 * Every fault this page exists for was found by hand on 25 September with ad-hoc SQL: a skip
 * rule quietly discarding the threads somebody had engaged with, 882 sources waiting on a
 * contract whose skills were off, a Teams link writing "unreachable" to a column nothing
 * reads. None of them was a missing number. Each was a number nobody was shown.
 *
 * So the page leads with what is wrong rather than with a chart, and a count is never shown
 * without the comparison that makes it mean something. A drop is only a drop against a
 * baseline, and a baseline this record cannot support yet is said to be missing instead of
 * being drawn as a confident line through four points.
 *
 * Counts, never ranks. Nothing here is per person except a mailbox that has gone silent,
 * which is a fault in the plumbing and is presented as one: no volume, no ordering, no
 * total. The delivery model forbids ranking people and a throughput figure per mailbox is
 * a ranking whatever the page is called.
 *
 * Pure, like portfolio.ts: rows in, a view out. The API loads the rows and decides who asks.
 */
import type { Iso } from "./commitment.js";
import { addCalendarDays, cap, dateOf, daysBetween } from "./dates.js";

// ------------------------------------------------------------------------------------------------ inputs

/**
 * Where a source got to. `ingested` counts every row observed that day; `processed`,
 * `skipped` and `pending` partition it. `raised` and `confirmed` count the commitments
 * that came out of those sources, filed under the source's own day and kind, so the funnel
 * reads down one column.
 *
 * `unresolved` counts what was read and reached nobody: an action whose owner, or the person
 * it was asked of, is not anybody Trellis can place. Its unit is the action, the same as
 * `raised`, because a statement the extraction found is either placed or it is one of these.
 * Without it a source that produced five commitments and a source that produced none are the
 * same row on this page, which is the one thing the funnel exists to tell apart.
 *
 * `built` is a marker: one row per day the rollup has rebuilt, so a day with no sources at
 * all is distinguishable from a day the rollup has never seen. Without it a window that
 * predates the rollup reads as a run of zeroes, which drags a baseline down and hides the
 * very drop the baseline is for.
 */
export type OpsStage = "built" | "ingested" | "processed" | "skipped" | "pending" | "raised" | "confirmed" | "unresolved";

export interface OpsDayRow {
  /** Calendar date, UTC. */
  day: string;
  /** Source kind, or "" on a `built` marker. */
  kind: string;
  /** "" when no program could be attributed. */
  programId: string;
  stage: OpsStage;
  /** The `meta.skipped` reason on a `skipped` row, the disposition on an `unresolved` one; "" elsewhere. */
  reason: string;
  n: number;
}

export type StuckBucket = "under1h" | "hours" | "days" | "week";
export type AttemptBucket = "none" | "few" | "many";

export interface StuckInput {
  /** Unprocessed sources by kind and age. */
  ages: { kind: string; bucket: StuckBucket; n: number }[];
  /** Retry attempts already spent on rows that are still unprocessed. */
  attempts: { bucket: AttemptBucket; n: number }[];
  /** A reason already written on a row that is held rather than closed. */
  held: { reason: string; n: number }[];
  /** Age of the oldest unprocessed source, in whole days. */
  oldestDays?: number | undefined;
}

export interface LinkLine {
  programId: string;
  programName?: string | undefined;
  kind: string;
  key: string;
  label?: string | undefined;
  instance?: string | undefined;
  /** Hours since the poll last ran. Absent means it has never run. */
  lastPollHours?: number | undefined;
  /** `last_error`: the far side did not answer. */
  error?: string | undefined;
  /** `last_note`: it answered and is doing nothing useful. Not an outage. */
  note?: string | undefined;
  count?: number | undefined;
}

export interface MissedCadenceLine {
  programId: string;
  programName?: string | undefined;
  cadenceId: string;
  /** What the model calls the cadence. The runs table holds only the id, so the API joins it on. */
  name?: string | undefined;
  n: number;
  /** Period start of the oldest miss in the window. */
  oldest: string;
}

/** A mailbox that was producing sources and has stopped. A fault, not a throughput figure. */
export interface QuietMailbox {
  upn: string;
  name?: string | undefined;
  /** Hours since the last source off this mailbox. */
  silentHours: number;
}

export interface OpsInput {
  /**
   * The rollup's rows. They may reach further back than `windowDays`, and should: the funnel
   * counts the window, but a baseline looks back three weeks to find comparable days, and a
   * fortnight of rows cannot answer a three-week question.
   */
  rows: OpsDayRow[];
  asOf: Iso;
  /** How many days the funnel counts over. */
  windowDays: number;
  /** When the rollup was last rebuilt, so the page can say how fresh it is. */
  builtAt?: Iso | undefined;
  stuck?: StuckInput | undefined;
  links?: LinkLine[] | undefined;
  cadences?: MissedCadenceLine[] | undefined;
  quiet?: QuietMailbox[] | undefined;
  /** How far back a missed cadence was counted, for the caption. */
  cadenceWindowDays?: number | undefined;
}

// ------------------------------------------------------------------------------------------------ thresholds

/** At most this many comparable days go into a baseline. */
export const BASELINE_DAYS = 7;
/** Searched this far back to find them, so a weekend can find other weekends. */
export const BASELINE_LOOKBACK = 21;
/** Below this the panel says it has no baseline instead of naming a median. */
export const BASELINE_MIN = 3;
/** A day under half its baseline is a drop. */
export const DROP_RATIO = 0.5;
/** A day over twice its baseline is a surge, which is usually a backfill. */
export const SURGE_RATIO = 2;
/** A median below this cannot fall meaningfully: one source instead of two is not a signal. */
export const DROP_FLOOR = 4;
/** A skip reason whose share of the day rises by this much is over-filtering until shown otherwise. */
export const SHARE_RISE = 0.1;
/** And it has to be more than a handful of messages. */
export const SHARE_FLOOR = 5;
/** This much of everything a skill extracted reaching nobody is a fault, not a bad week. */
export const UNRESOLVED_SHARE = 0.25;
/** And more than a handful of actions, so one page naming one stranger is not a fault. */
export const UNRESOLVED_FLOOR = 5;

// ------------------------------------------------------------------------------------------------ output

export interface FunnelLine {
  kind: string;
  ingested: number;
  processed: number;
  skipped: number;
  pending: number;
  raised: number;
  confirmed: number;
  /** Actions read that nobody could be attributed. Counted like `raised`, not like a source. */
  unresolved: number;
  /** processed / ingested. Undefined when nothing was ingested; a rate over nothing is not zero. */
  readRate?: number | undefined;
  /** raised / processed: how much of what was read turned into something to answer. */
  raiseRate?: number | undefined;
  /** unresolved / (raised + unresolved): of the actions found, the share that reached nobody. */
  unresolvedShare?: number | undefined;
}

export interface SkipLine {
  reason: string;
  /** Over the whole window. */
  n: number;
  /** Of everything ingested in the window. */
  share: number;
  /** On the day under comparison. */
  dayN: number;
  dayShare?: number | undefined;
  /** Median share of the same reason across the baseline days. */
  baselineShare?: number | undefined;
  /** dayShare - baselineShare, in share points. */
  delta?: number | undefined;
  rising: boolean;
  /** Absent from every baseline day and here in numbers: a rule that has just started biting. */
  newly: boolean;
}

export interface VolumeDay { day: string; n: number; weekend: boolean }

export interface VolumeSignal {
  /** A source kind, or "" for every kind together. */
  kind: string;
  /** The most recent day the rollup has built in full. */
  day?: string | undefined;
  n?: number | undefined;
  median?: number | undefined;
  /** How many comparable days the baseline found, and how many it needs. */
  comparable: number;
  needed: number;
  state: "no-baseline" | "steady" | "down" | "up";
}

export interface OpsAlert {
  id: string;
  tone: "bad" | "watch";
  title: string;
  detail: string;
}

export interface OpsView {
  asOf: Iso;
  windowDays: number;
  builtAt?: Iso | undefined;
  /** What is wrong, worst first. Empty is the good case and the screen says so in words. */
  alerts: OpsAlert[];
  funnel: FunnelLine[];
  total: FunnelLine;
  skips: SkipLine[];
  /**
   * Why an action reached nobody, over the window. The reasons partition `total.unresolved`:
   * a statement has exactly one disposition, so unlike a source it cannot be in two of these.
   */
  unresolved: { reason: string; n: number }[];
  days: VolumeDay[];
  signals: VolumeSignal[];
  /** Today so far. Never compared: a part-day against whole days always looks like a drop. */
  today: { day: string; n: number; partial: true };
  /** The day the signals are about, and how much comparable history there is at all. */
  baseline: { day?: string | undefined; comparable: number; needed: number; lookbackDays: number };
  /**
   * How many of the window's days the aggregate has actually counted, and the earliest it
   * holds at all. Asked for thirty days and holding six, the funnel is six days of counts;
   * saying "thirty days" over it would read as a quiet month rather than a young record.
   */
  coverage: { days: number; asked: number; earliest?: string | undefined };
  stuck: {
    total: number;
    ages: { kind: string; bucket: StuckBucket; n: number }[];
    attempts: { bucket: AttemptBucket; n: number }[];
    held: { reason: string; n: number }[];
    oldestDays?: number | undefined;
  };
  links: LinkLine[];
  cadences: MissedCadenceLine[];
  cadenceWindowDays: number;
  quiet: QuietMailbox[];
  /** What this page cannot answer, because nothing records it. Stated rather than implied. */
  blindSpots: string[];
}

// ------------------------------------------------------------------------------------------------ derivation

export function median(xs: number[]): number | undefined {
  if (!xs.length) return undefined;
  const s = [...xs].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid]! : (s[mid - 1]! + s[mid]!) / 2;
}

export function isWeekend(day: string): boolean {
  const d = new Date(`${day}T00:00:00Z`).getUTCDay();
  return d === 0 || d === 6;
}

/** "YYYY-MM-DD" for a UTC calendar date built from year/month(1-12)/day. */
function isoOf(year: number, month: number, day: number): string {
  return new Date(Date.UTC(year, month - 1, day)).toISOString().slice(0, 10);
}

/** The `n`th occurrence of `weekday` (0=Sun..6=Sat) in `month` (1-12) of `year`. */
function nthWeekday(year: number, month: number, weekday: number, n: number): string {
  const firstDow = new Date(Date.UTC(year, month - 1, 1)).getUTCDay();
  const day = 1 + ((weekday - firstDow + 7) % 7) + (n - 1) * 7;
  return isoOf(year, month, day);
}

/** The last occurrence of `weekday` (0=Sun..6=Sat) in `month` (1-12) of `year`. */
function lastWeekday(year: number, month: number, weekday: number): string {
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const lastDow = new Date(Date.UTC(year, month, 0)).getUTCDay();
  return isoOf(year, month, lastDay - ((lastDow - weekday + 7) % 7));
}

/**
 * A fixed-date holiday, shifted to the day it is observed: the preceding Friday when it falls
 * on a Saturday, the following Monday when it falls on a Sunday. New Year's Day is the one
 * holiday this can carry across a year boundary: when 1 January is a Saturday, it is observed
 * on 31 December of the prior year.
 */
function observed(day: string): string {
  const dow = new Date(`${day}T00:00:00Z`).getUTCDay();
  if (dow === 6) return addCalendarDays(day, -1);
  if (dow === 0) return addCalendarDays(day, 1);
  return day;
}

/**
 * The 11 OPM holidays (5 U.S.C. § 6103) observed in `year`, computed by rule rather than kept
 * as a maintained list, so a new year is correct for free. The "nth weekday of month" holidays
 * are always a weekday by construction and need no shift; the five fixed-date holidays do.
 */
function federalHolidaysForYear(year: number): string[] {
  return [
    observed(isoOf(year, 1, 1)),   // New Year's Day
    observed(isoOf(year, 6, 19)),  // Juneteenth National Independence Day
    observed(isoOf(year, 7, 4)),   // Independence Day
    observed(isoOf(year, 11, 11)), // Veterans Day
    observed(isoOf(year, 12, 25)), // Christmas Day
    nthWeekday(year, 1, 1, 3),     // Birthday of Martin Luther King, Jr.: 3rd Monday of January
    nthWeekday(year, 2, 1, 3),     // Washington's Birthday: 3rd Monday of February
    lastWeekday(year, 5, 1),       // Memorial Day: last Monday of May
    nthWeekday(year, 9, 1, 1),     // Labor Day: 1st Monday of September
    nthWeekday(year, 10, 1, 2),    // Columbus Day: 2nd Monday of October
    nthWeekday(year, 11, 4, 4),    // Thanksgiving Day: 4th Thursday of November
  ];
}

/**
 * Whether `day` (YYYY-MM-DD) is one of the 11 OPM federal holidays, observed. Checked against
 * both `day`'s own year and the next year, because a Saturday New Year's Day is observed on 31
 * December of the year before: a 31 December that is itself a holiday belongs to next year's
 * list, not this year's.
 */
export function isUsFederalHoliday(day: string): boolean {
  const year = Number(day.slice(0, 4));
  return federalHolidaysForYear(year).includes(day) || federalHolidaysForYear(year + 1).includes(day);
}

/**
 * Which earlier days a day may be compared with: built, the same kind of day, and recent.
 *
 * Weekday and weekend are held apart because mail volume halves at the weekend, and a
 * Saturday measured against a week of weekdays reads as an outage every week. The cost is
 * that a weekend baseline needs three weekend days to exist, which is why the lookback is
 * three weeks rather than one and why the panel names how many days it has.
 */
export function baselineDays(subject: string, built: Set<string>): string[] {
  const weekend = isWeekend(subject);
  const out: string[] = [];
  for (let back = 1; back <= BASELINE_LOOKBACK && out.length < BASELINE_DAYS; back++) {
    const day = addCalendarDays(subject, -back);
    if (built.has(day) && isWeekend(day) === weekend) out.push(day);
  }
  return out;
}

function signalFor(kind: string, subject: string | undefined, built: Set<string>, countOn: (day: string) => number): VolumeSignal {
  if (!subject) return { kind, comparable: 0, needed: BASELINE_MIN, state: "no-baseline" };
  const days = baselineDays(subject, built);
  const n = countOn(subject);
  const base = { kind, day: subject, n, comparable: days.length, needed: BASELINE_MIN };
  if (days.length < BASELINE_MIN) return { ...base, state: "no-baseline" };
  const mid = median(days.map(countOn))!;
  if (mid < DROP_FLOOR) return { ...base, median: mid, state: "no-baseline" };
  if (n < mid * DROP_RATIO) return { ...base, median: mid, state: "down" };
  if (n > mid * SURGE_RATIO) return { ...base, median: mid, state: "up" };
  return { ...base, median: mid, state: "steady" };
}

const EMPTY_FUNNEL = (kind: string): FunnelLine => ({ kind, ingested: 0, processed: 0, skipped: 0, pending: 0, raised: 0, confirmed: 0, unresolved: 0 });

function rate(part: number, whole: number): number | undefined {
  return whole > 0 ? part / whole : undefined;
}

export function deriveOps(input: OpsInput): OpsView {
  const asOf = input.asOf;
  const today = dateOf(asOf);
  const windowDays = input.windowDays;
  const from = addCalendarDays(today, -(windowDays - 1));

  // Two ranges, deliberately. The funnel and the skip mix count the window the reader
  // asked for; a baseline reaches back `BASELINE_LOOKBACK` days to find comparable days,
  // and cutting it at the window would silently give a fortnight's page a thirteen-day
  // lookback and fewer comparable days than the rollup actually holds.
  const known = input.rows.filter((r) => r.day <= today);
  const rows = known.filter((r) => r.day >= from);

  const built = new Set(known.filter((r) => r.stage === "built").map((r) => r.day));
  // Today is still being written. The comparison is about the last day that is finished.
  const subject = [...built].filter((d) => d < today).sort().pop();
  // The chart shows the window; the baseline may reach behind it.
  const builtInWindow = new Set([...built].filter((d) => d >= from));

  // ---- the funnel, by kind
  const byKind = new Map<string, FunnelLine>();
  for (const r of rows) {
    if (r.stage === "built") continue;
    const line = byKind.get(r.kind) ?? EMPTY_FUNNEL(r.kind);
    line[r.stage] += r.n;
    byKind.set(r.kind, line);
  }
  const funnel = [...byKind.values()]
    .map((l) => ({
      ...l, readRate: rate(l.processed, l.ingested), raiseRate: rate(l.raised, l.processed),
      unresolvedShare: rate(l.unresolved, l.raised + l.unresolved),
    }))
    .sort((a, b) => b.ingested - a.ingested || a.kind.localeCompare(b.kind));
  const total = funnel.reduce<FunnelLine>((a, l) => ({
    kind: "", ingested: a.ingested + l.ingested, processed: a.processed + l.processed, skipped: a.skipped + l.skipped,
    pending: a.pending + l.pending, raised: a.raised + l.raised, confirmed: a.confirmed + l.confirmed,
    unresolved: a.unresolved + l.unresolved,
  }), EMPTY_FUNNEL(""));
  total.readRate = rate(total.processed, total.ingested);
  total.raiseRate = rate(total.raised, total.processed);
  total.unresolvedShare = rate(total.unresolved, total.raised + total.unresolved);

  // ---- volume per day, and the drop test
  // Over `known`, not `rows`: these maps answer the baseline's questions as well as the
  // chart's, and the baseline asks about days behind the window.
  const ingestedOn = new Map<string, number>();
  const ingestedOnByKind = new Map<string, Map<string, number>>();
  for (const r of known) {
    if (r.stage !== "ingested") continue;
    ingestedOn.set(r.day, (ingestedOn.get(r.day) ?? 0) + r.n);
    const perKind = ingestedOnByKind.get(r.kind) ?? new Map<string, number>();
    perKind.set(r.day, (perKind.get(r.day) ?? 0) + r.n);
    ingestedOnByKind.set(r.kind, perKind);
  }
  // Complete days only. Today has a field of its own because it is a part day and is never
  // compared; drawing it in both places put the same column on the chart twice.
  const days: VolumeDay[] = [...builtInWindow].filter((d) => d < today).sort().map((day) => ({ day, n: ingestedOn.get(day) ?? 0, weekend: isWeekend(day) }));
  const signals: VolumeSignal[] = [
    signalFor("", subject, built, (d) => ingestedOn.get(d) ?? 0),
    // Only kinds that carried something in the window: a signal about a kind Trellis does
    // not read on this deployment is noise on the page.
    ...funnel.filter((l) => l.ingested > 0).map((l) => signalFor(l.kind, subject, built, (d) => ingestedOnByKind.get(l.kind)?.get(d) ?? 0)),
  ];

  // ---- skip reasons: the window's mix, and the day's share against its baseline
  const base = subject ? baselineDays(subject, built) : [];
  // The window's totals, and the per-day shares the baseline needs, which reach behind it.
  const skippedIn = new Map<string, number>();
  const skippedOn = new Map<string, Map<string, number>>();  // day -> reason -> n
  for (const r of known) {
    if (r.stage !== "skipped") continue;
    const reason = r.reason || "(no reason recorded)";
    if (r.day >= from) skippedIn.set(reason, (skippedIn.get(reason) ?? 0) + r.n);
    const perDay = skippedOn.get(r.day) ?? new Map<string, number>();
    perDay.set(reason, (perDay.get(reason) ?? 0) + r.n);
    skippedOn.set(r.day, perDay);
  }
  const shareOn = (day: string, reason: string): number | undefined => {
    const ing = ingestedOn.get(day) ?? 0;
    return ing > 0 ? (skippedOn.get(day)?.get(reason) ?? 0) / ing : undefined;
  };
  const enoughBaseline = base.length >= BASELINE_MIN;
  const skips: SkipLine[] = [...skippedIn.entries()].map(([reason, n]) => {
    const dayN = subject ? (skippedOn.get(subject)?.get(reason) ?? 0) : 0;
    const dayShare = subject ? shareOn(subject, reason) : undefined;
    const baseShares = enoughBaseline ? base.map((d) => shareOn(d, reason)).filter((x): x is number => x !== undefined) : [];
    const baselineShare = baseShares.length >= BASELINE_MIN ? median(baseShares) : undefined;
    const delta = dayShare !== undefined && baselineShare !== undefined ? dayShare - baselineShare : undefined;
    return {
      reason, n, share: total.ingested > 0 ? n / total.ingested : 0, dayN,
      ...(dayShare !== undefined ? { dayShare } : {}),
      ...(baselineShare !== undefined ? { baselineShare } : {}),
      ...(delta !== undefined ? { delta } : {}),
      rising: delta !== undefined && delta >= SHARE_RISE && dayN >= SHARE_FLOOR,
      newly: baselineShare === 0 && dayN >= SHARE_FLOOR,
    };
  }).sort((a, b) => b.n - a.n || a.reason.localeCompare(b.reason));

  // ---- what was read and reached nobody, by which kind of unresolution
  const unresolvedIn = new Map<string, number>();
  for (const r of rows) {
    if (r.stage !== "unresolved") continue;
    const reason = r.reason || "(no disposition recorded)";
    unresolvedIn.set(reason, (unresolvedIn.get(reason) ?? 0) + r.n);
  }
  const unresolved = [...unresolvedIn.entries()]
    .map(([reason, n]) => ({ reason, n }))
    .sort((a, b) => b.n - a.n || a.reason.localeCompare(b.reason));

  // ---- what is stuck
  const stuckIn = input.stuck ?? { ages: [], attempts: [], held: [] };
  const stuck = {
    total: stuckIn.ages.reduce((a, x) => a + x.n, 0),
    ages: [...stuckIn.ages].sort((a, b) => b.n - a.n || a.kind.localeCompare(b.kind)),
    attempts: stuckIn.attempts,
    held: [...stuckIn.held].sort((a, b) => b.n - a.n || a.reason.localeCompare(b.reason)),
    ...(stuckIn.oldestDays !== undefined ? { oldestDays: stuckIn.oldestDays } : {}),
  };

  const links = input.links ?? [];
  const cadences = input.cadences ?? [];
  const quiet = input.quiet ?? [];

  return {
    asOf, windowDays,
    ...(input.builtAt ? { builtAt: input.builtAt } : {}),
    alerts: alertsFor({ signals, skips, stuck, links, cadences, quiet, subject, asOf, unresolved, raised: total.raised }),
    funnel, total, skips, unresolved, days, signals,
    today: { day: today, n: ingestedOn.get(today) ?? 0, partial: true },
    baseline: { ...(subject ? { day: subject } : {}), comparable: base.length, needed: BASELINE_MIN, lookbackDays: BASELINE_LOOKBACK },
    coverage: { days: builtInWindow.size, asked: windowDays, ...(built.size ? { earliest: [...built].sort()[0]! } : {}) },
    stuck, links, cadences,
    cadenceWindowDays: input.cadenceWindowDays ?? 30,
    quiet,
    blindSpots: BLIND_SPOTS,
  };
}

/**
 * What nothing in the record can answer, said out loud so the page is not read as complete.
 *
 * The first two are the same gap: `pollMail` counts what it listed and what it refused
 * before a row exists, logs both and throws them away, so the page can show what was
 * ingested and never what was offered. A counter per pass, or a `poll_passes` row, would
 * close it; that is the ingestion path and belongs to whoever owns it.
 */
const BLIND_SPOTS = [
  "How many messages each poll listed before anything was written. The pass counts them and only logs them, so the page starts at the first stored row.",
  "Why a message was refused before a row existed — a malformed sender, a non-conversational note with no topic on it. Those never reach the funnel.",
  "How long a source waited between arriving and being read. The timestamps for that are being added by the latency work; until they land the ages here are measured from when Trellis saw it, not from when it happened.",
];

/** A share as a percentage, never rounding a real number down to nothing. */
function pct(x: number): string {
  return x > 0 && x < 0.005 ? "under 1%" : `${Math.round(x * 100)}%`;
}

/**
 * The page's first job. A count nobody reads is what let every one of these faults run for
 * a day, so the ones that matter are turned into sentences and put at the top, worst first.
 */
export function alertsFor(x: {
  signals: VolumeSignal[];
  skips: SkipLine[];
  /** Actions read that reached nobody, by disposition, and how many did reach somebody. */
  unresolved?: { reason: string; n: number }[] | undefined;
  raised?: number | undefined;
  stuck: { total: number; oldestDays?: number | undefined };
  links: LinkLine[];
  cadences: MissedCadenceLine[];
  quiet: QuietMailbox[];
  subject?: string | undefined;
  asOf: Iso;
}): OpsAlert[] {
  const out: OpsAlert[] = [];

  const overall = x.signals.find((s) => s.kind === "");
  if (overall?.state === "down" && overall.median !== undefined) {
    out.push({
      id: "volume", tone: "bad",
      title: `Ingestion on ${overall.day} was ${overall.n} against a usual ${overall.median}`,
      detail: `Less than half the median of the ${overall.comparable} comparable days before it. Either less arrived or something is refusing more of it; the skip mix below says which.`,
    });
  }
  for (const s of x.signals.filter((s) => s.kind && s.state === "down")) {
    out.push({
      id: `volume:${s.kind}`, tone: "watch",
      title: `${cap(s.kind.replace(/-/g, " "))} dropped to ${s.n} on ${s.day}, against a usual ${s.median}`,
      detail: `One kind fell while the whole did not, which usually means one connector rather than a quiet day.`,
    });
  }

  for (const s of x.skips.filter((s) => s.newly || s.rising)) {
    const share = s.dayShare === undefined ? "" : ` It took ${pct(s.dayShare)} of the day.`;
    out.push({
      id: `skip:${s.reason}`, tone: s.newly ? "bad" : "watch",
      title: s.newly
        ? `"${s.reason}" is dropping ${s.dayN} a day and was dropping none`
        : `"${s.reason}" is taking a bigger share: up ${Math.round((s.delta ?? 0) * 100)} points`,
      detail: `${s.n} in the window.${share} A share that grows while the total holds is over-filtering, not a quiet week. Read the rule before widening it.`,
    });
  }

  // The blind spot this page had until 26 September: every one of these was read, understood
  // and reached nobody, and no other count moves when it happens. A funnel holding steady
  // while less arrives on anybody's record looks like nothing happening at all.
  const lost = (x.unresolved ?? []).reduce((a, u) => a + u.n, 0);
  const found = lost + (x.raised ?? 0);
  if (lost >= UNRESOLVED_FLOOR && found > 0 && lost / found >= UNRESOLVED_SHARE) {
    const top = (x.unresolved ?? [])[0];
    const meaning = top ? unresolvedMeaning(top.reason) : "";
    out.push({
      id: "unresolved", tone: "bad",
      title: `${lost} of ${found} actions read could not be attributed to anybody`,
      detail: `${pct(lost / found)} of what the skills found reached nobody${top ? `, most of it "${top.reason}"${meaning ? `: ${meaning}` : ""}` : ""}. Nothing was dropped and nothing is waiting, so no other count on this page moves when this happens.`,
    });
  }

  if (x.stuck.total > 0) {
    const old = x.stuck.oldestDays ?? 0;
    // A handful in flight is the normal state of a poller. A backlog with age is not.
    const bad = x.stuck.total >= 100 || old >= 2;
    if (bad) {
      out.push({
        id: "stuck", tone: x.stuck.total >= 100 ? "bad" : "watch",
        title: `${x.stuck.total} sources are waiting to be read, the oldest for ${old === 1 ? "a day" : `${old} days`}`,
        detail: `A source that cannot be processed is held on purpose, so a configuration gap can be fixed and the message read. Held is not finished: something has to change for these to move.`,
      });
    }
  }

  for (const l of x.links.filter((l) => l.error)) {
    out.push({
      id: `link:${l.programId}:${l.kind}:${l.key}`, tone: "bad",
      title: `${l.label ?? l.key} has not answered`,
      detail: `${cap(l.kind.replace(/-/g, " "))} on ${l.programName ?? l.programId}: ${l.error}`,
    });
  }
  for (const l of x.links.filter((l) => !l.error && l.note)) {
    out.push({
      id: `link-note:${l.programId}:${l.kind}:${l.key}`, tone: "watch",
      title: `${l.label ?? l.key} answers and reads nothing`,
      detail: `${cap(l.kind.replace(/-/g, " "))} on ${l.programName ?? l.programId}: ${l.note}`,
    });
  }

  const missed = x.cadences.reduce((a, c) => a + c.n, 0);
  if (missed > 0) {
    out.push({
      id: "cadences", tone: "watch",
      title: `${missed} cadence ${missed === 1 ? "period" : "periods"} closed with nothing to show for ${missed === 1 ? "it" : "them"}`,
      detail: `A missed period is on the contract's record already; it is here because a run of them is usually one broken thing, not many.`,
    });
  }

  for (const q of x.quiet) {
    out.push({
      id: `quiet:${q.upn}`, tone: "watch",
      title: `Nothing has come off ${q.upn} for ${Math.round(q.silentHours)} hours`,
      detail: `This mailbox was producing sources and has stopped. Usually the opt-in group, a revoked permission, or the poll itself. It is not a statement about the person.`,
    });
  }

  return out;
}

/** A skip reason as a sentence, where the product knows what the rule was. */
export function skipMeaning(reason: string): string {
  if (reason.startsWith("precedence:")) return "a bulk or list header";
  if (reason.startsWith("auto-submitted:")) return "an automation stamp on the message";
  switch (reason) {
    case "list": return "a mailing-list header";
    case "auto-reply": return "an out-of-office or automatic reply";
    case "sender": return "the address is a no-reply or notification mailbox";
    case "service-account": return "the address is a service account";
    case "first-contact": return "a first message from a company nobody here has dealt with";
    case "answered": return "the owner had already replied on the thread";
    case "content-filter": return "the model refused the text";
    case "skills-off": return "the contract has not switched skills on";
    case "no-message-id": return "the message carried no identifier to fetch it by";
    case "malformed-sender": return "the sender address was not an address";
    case "incomplete-transcript-reference": return "the transcript reference was missing part of itself";
    default: return reason.startsWith("gave-up-after-") ? "the retries ran out" : "";
  }
}

/** What an unresolved disposition means, in the words somebody who has to fix it would use. */
export function unresolvedMeaning(reason: string): string {
  switch (reason) {
    case "unresolved-author": return "the person who promised it is not on a roster Trellis can read";
    case "unresolved-recipient": return "the person they asked is not on a roster Trellis can read";
    case "unresolved-attribution": return "a page said what would happen and named nobody it could belong to";
    default: return "";
  }
}

/** Age buckets, in the words the screen uses. */
export function stuckBucketLabel(b: StuckBucket): string {
  switch (b) {
    case "under1h": return "under an hour";
    case "hours": return "1 to 24 hours";
    case "days": return "1 to 7 days";
    case "week": return "over a week";
  }
}

export function attemptBucketLabel(b: AttemptBucket): string {
  switch (b) {
    case "none": return "never tried";
    case "few": return "1 to 3 tries";
    case "many": return "4 or more tries";
  }
}

/** How stale the aggregate is, for the caption under the heading. */
export function rollupAge(builtAt: Iso | undefined, asOf: Iso): string {
  if (!builtAt) return "not built yet";
  const mins = Math.max(0, Math.round((Date.parse(asOf) - Date.parse(builtAt)) / 60_000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const days = daysBetween(builtAt, asOf);
  if (days >= 1) return `${days === 1 ? "a day" : `${days} days`} ago`;
  const hours = Math.round(mins / 60);
  return `${hours === 1 ? "an hour" : `${hours} hours`} ago`;
}
